/*
  FD brand: paging the photographs on a collection card, in place.

  The card ships every frame it can page through already in the DOM, side by
  side inside a horizontal snap scroller, so this file never builds, fetches or
  reorders anything -- with JavaScript off the card still shows the featured
  photograph and simply cannot be paged. That is also why paging is done by
  scrolling the container rather than by toggling classes: the browser's own
  horizontal scroll is the swipe gesture on a phone, for free and with native
  momentum, and a swipe that scrolls is a swipe the browser will not turn into
  a click on the product link underneath.

  The arrows are siblings of that link, never inside it: a <button> nested in
  an <a> is invalid markup and unreachable by keyboard. Because they sit
  outside, a click on one cannot navigate in the first place -- the guards in
  step() are belt and braces for a card markup change that puts them back in.

  The position is published on the card as `data-fd-card-at` rather than kept
  to itself, because two lines under the photograph (the fabric chip and the
  "shown in" caption) describe the FEATURED shot and stop being true of what is
  on screen once the shopper pages on. fd-collection.css fades them on that
  attribute.
*/
if (!customElements.get('fd-card-media')) {
  customElements.define(
    'fd-card-media',
    class FdCardMedia extends HTMLElement {
      connectedCallback() {
        this.frames = this.querySelector('[data-fd-card-frames]');
        if (!this.frames) return;

        this.slides = Array.from(this.frames.children);
        // A card with one photograph is not a slider: leaving now keeps the
        // attribute off the card, so nothing fades and nothing is bound.
        if (this.slides.length < 2) return;

        this.card = this.closest('.fd-card');
        this.querySelector('[data-fd-card-prev]')?.addEventListener('click', (event) => this.step(event, -1));
        this.querySelector('[data-fd-card-next]')?.addEventListener('click', (event) => this.step(event, 1));

        // Swiping moves the scroller without asking us, so the published
        // position is read back off the scroll rather than counted from
        // clicks -- otherwise a swipe and an arrow would disagree.
        this.frames.addEventListener('scroll', () => this.queueSync(), { passive: true });
        this.sync();
      }

      disconnectedCallback() {
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      index() {
        const width = this.frames.clientWidth;
        if (!width) return 0;
        return Math.min(this.slides.length - 1, Math.max(0, Math.round(this.frames.scrollLeft / width)));
      }

      step(event, direction) {
        event.preventDefault();
        event.stopPropagation();
        // Wrap-around, as the product gallery does: the set is small, and an
        // arrow that greys out at each end reads as a fault rather than an
        // edge.
        const total = this.slides.length;
        const next = (this.index() + direction + total) % total;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.frames.scrollTo({ left: next * this.frames.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      }

      // Scroll fires per frame while a swipe is in flight; one read per paint
      // is enough and keeps the gesture smooth.
      queueSync() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }

      sync() {
        this.frame = null;
        this.card?.setAttribute('data-fd-card-at', this.index());
      }
    }
  );
}
